import type { AppContext, ChatBrotherhood } from '@cruz/shared';

/**
 * Opening suggestions, adapted to the day: what makes sense to ask on a procession
 * day is not what makes sense in October. Examples only name things, never data.
 */
export function suggestions(ctx: AppContext | null): string[] {
  const general = [
    '¿A qué hora pasa la Borriquita por Sierpes?',
    '¿Desde qué iglesia sale la Esperanza de Triana?',
  ];
  if (!ctx) return ['¿Qué hermandades salen el Domingo de Ramos?', ...general];

  const today = ctx.season.days.find((d) => d.date === ctx.now.slice(0, 10));
  const tomorrowDate = addDays(ctx.now.slice(0, 10), 1);
  const tomorrow = ctx.season.days.find((d) => d.date === tomorrowDate);

  if (ctx.in_progress.length > 0) {
    return [
      '¿Qué cofradías están ahora en la calle?',
      `Hoy es ${ctx.in_progress[0]}: ¿qué hermandades salen?`,
      'Estoy en la Campana, ¿qué voy a ver pasar en la próxima hora?',
    ];
  }
  if (today) {
    return [
      `Hoy es ${today.label}: ¿qué hermandades salen?`,
      '¿Qué cofradías están ahora en la calle?',
      ...(tomorrow ? [`¿Qué hermandades salen mañana, ${tomorrow.label}?`] : general.slice(0, 1)),
    ];
  }
  if (tomorrow) {
    return [`Mañana es ${tomorrow.label}: ¿qué hermandades salen?`, ...general];
  }
  const before = ctx.now.slice(0, 10) < ctx.season.first_day;
  return [
    before
      ? `¿Cuándo empieza la Semana Santa de ${ctx.season.year}?`
      : '¿Qué hermandades salen el Domingo de Ramos?',
    ...general,
  ];
}

function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** A follow-up question and the tools whose answer already covers it. */
interface FollowUp {
  text: string;
  coveredBy: string[];
}

const MAX_FOLLOW_UPS = 3;

/**
 * Follow-up questions about the brotherhood the last answer was about, leaving out
 * what that answer already covered and what was already asked.
 */
export function followUps(
  brotherhood: ChatBrotherhood,
  { ctx, tools = [], asked = [] }: { ctx: AppContext | null; tools?: string[]; asked?: string[] },
): string[] {
  const n = inSentence(brotherhood.name);
  const today = ctx?.season.days.find((d) => d.date === ctx.now.slice(0, 10))?.label;
  const itsDay = brotherhood.day !== null && brotherhood.day === today;
  const all: FollowUp[] = [
    ...(itsDay ? [{ text: `¿Ha salido ya ${n}?`, coveredBy: ['day_status'] }] : []),
    {
      text: `¿A qué hora sale ${n} y a qué hora se recoge?`,
      coveredBy: ['brotherhood_schedule', 'itinerary'],
    },
    { text: `¿Qué recorrido hace ${n}?`, coveredBy: ['itinerary'] },
    {
      text: `¿A qué hora pasa ${n} por la Campana?`,
      coveredBy: ['passing_time', 'brotherhood_schedule', 'itinerary'],
    },
    { text: `¿De qué iglesia sale ${n} y cuántos pasos lleva?`, coveredBy: ['brotherhood_info'] },
  ];
  const seen = new Set(asked.map(normalize));
  return all
    .filter((f) => !f.coveredBy.some((t) => tools.includes(t)) && !seen.has(normalize(f.text)))
    .slice(0, MAX_FOLLOW_UPS)
    .map((f) => f.text);
}

/** "La Borriquita" → "la Borriquita", to read well inside a question. */
function inSentence(name: string): string {
  return /^(El|La|Los|Las) /u.test(name) ? name[0]!.toLowerCase() + name.slice(1) : name;
}

const normalize = (s: string) => s.trim().toLocaleLowerCase('es');
